import React, { useState, useContext, useEffect, DragEvent, KeyboardEvent } from 'react';
import { FormikErrors, FormikProps } from 'formik';
import { DragIndicator } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import { Box, IconButton, InputBase, List, ListItem, ListItemButton, ListItemText, Skeleton, Stack, Switch } from '@mui/material';
import { NON_EXISTING } from '../../../utility/constants';
import { isObjectsEqual } from '../../../utility/helper';
import { IFormikValuesForRuleAndOverridesPerSetting, IIneligibleSettingsContext, IIneligibleSettingDetail, IFormikValuesForUPCRuleAndOverridesPerSetting, IIneligibleSettingsPermissions } from '../../../interfaces/ineligibleSettingInterface';
import { IneligibleSettingsContext } from '../../../context/ineligibleSettingsContext';
import { validateIneligibleSetting } from '../../../schemas/ineligibleSettingsSchema';
import styles from './styles';
import { checkOverrideErrors } from '../ineligible-setting-details';

export interface DraggableListItem {
  recordId: number;
  name: string;
  defaultName: string;
  value: string;
  disabled: boolean;
  isRenamed: boolean;
}

interface DraggableListProps {
  formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting> | FormikProps<IFormikValuesForUPCRuleAndOverridesPerSetting>;
  draggableListItems: DraggableListItem[];
  setDraggableListItems: (draggableListItem: DraggableListItem[]) => void;
  onItemToggle: (item: DraggableListItem) => void;
  onItemDrop: (draggedItem: DraggableListItem) => void;
  onRenameCommit: (item: DraggableListItem, newName: string) => void;
  onResetClick: (item: DraggableListItem) => void;
}

interface DraggableRowProps {
  formik: FormikProps<IFormikValuesForRuleAndOverridesPerSetting> | FormikProps<IFormikValuesForUPCRuleAndOverridesPerSetting>;
  row: DraggableListItem;
  index: number;
  isDragging: boolean;
  hasError: boolean;
  onItemToggle: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => void;
  onDragStart: (event: DragEvent<HTMLButtonElement>, index: number) => void;
  onDragEnd: () => void;
  onDragOver: (hoveredIndex: number) => void;
  onDrop: () => void;
  onRenameCommit: (item: DraggableListItem, newName: string) => void;
  onResetClick: (item: DraggableListItem) => void;
}

/**
 * Component represents a draggable row inside the DraggableList for Ineligibles.
 * @param props The props for the DraggableRow component.
 * @returns The DraggableRow component JSX.
 */
const Row: React.FC<DraggableRowProps> = (props) => {
  const { formik, row, index, isDragging, hasError, onItemToggle, onDragStart, onDragEnd, onDragOver, onDrop, onRenameCommit, onResetClick } = props;

  const {
    selectedIneligibleIndex: ineligibleIndex,
    setSelectedIneligibleIndex,
    permissions,
    isHidingInactiveIneligibles,
    setIsHidingInactiveIneligibles,
  }                                                   = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;

  const [isHovered, setIsHovered]                     = useState<boolean>(false);
  const [isEditing, setIsEditing]                     = useState<boolean>(false);
  const [editValue, setEditValue]                     = useState<string>(row.name);

  useEffect(() => { if (!isEditing) { setEditValue(row.name); } }, [row.name, isEditing]);

  const canRename = (permissions as IIneligibleSettingsPermissions).canUpdateCriteriaAndSettings;

  /**
   * This function handles the click event for ineligible list item.
   */
  const handleListItemClick = () => {
    if (isEditing) { return; }
    validateIneligibleSetting(formik, `ineligibleSettingDetails[${ineligibleIndex}]`);
    setSelectedIneligibleIndex(index);
  };

  /**
   * This function handles the click event for the drag and drop icon of an ineligible list item.
   */
  const handleDragAndDropIconClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (row.disabled) { event.stopPropagation(); }
  };

  /**
   * This function checks if the row is selected.
   * @returns True if the row is selected, false otherwise.
   */
  const isRowSelected = () => {
    if (ineligibleIndex === NON_EXISTING) { return false; }
    return props.formik.values.ineligibleSettingDetails[ineligibleIndex]?.ineligibleSetting.ineligibleSettingId === row.recordId;
  };

  /**
   * This function gets the styles for the ListItem based on its state.
   * @returns The styles object for the ListItem.
   */
  const getListItemStyles = () => {
    if (hasError && isDragging) { return styles.listItemForDraggingWithError; }
    if (hasError) { return styles.listItemWithError; }
    if (isDragging) { return styles.listItemForDragging; }
    return styles.listItem;
  };

  /**
   * This function starts inline edit mode for the row's name.
   */
  const startEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setEditValue(row.name);
    setIsEditing(true);
  };

  /**
   * This function cancels inline edit mode without persisting any change.
   */
  const cancelEdit = () => {
    setEditValue(row.name);
    setIsEditing(false);
  };

  /**
   * This function commits the inline edit, if the trimmed value is non-empty and has changed.
   */
  const commitEdit = () => {
    const trimmedValue = editValue.trim();
    setIsEditing(false);
    if (trimmedValue === '' || trimmedValue === row.name) { setEditValue(row.name); return; }
    onRenameCommit(row, trimmedValue);
  };

  /**
   * This function handles keyboard interaction while editing the row's name.
   */
  const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') { event.preventDefault(); commitEdit(); }
    if (event.key === 'Escape') { event.preventDefault(); cancelEdit(); }
  };

  /**
   * This function handles the click event for the reset-to-default icon.
   */
  const handleResetClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    onResetClick(row);
  };

  /**
   * This function renders the ineligible list item.
   * @returns The rendered component.
   */
  const renderListItem = () => {
    if (isHidingInactiveIneligibles && row.disabled) { return null }
    return (
      <ListItem
        dense
        disablePadding
        sx={getListItemStyles()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <ListItemButton
          tabIndex={-1}
          selected={isRowSelected()}
          disabled={row.disabled}
          disableRipple
          disableTouchRipple
          sx={{ ...styles.listItemButton }}
          onClick={handleListItemClick}
        >
          <IconButton
            draggable
            edge='end'
            aria-label='Drag and drop icon'
            disabled={!(permissions as IIneligibleSettingsPermissions).canReorderList}
            onClick={(event) => handleDragAndDropIconClick(event)}
            onMouseOver={() => setIsHidingInactiveIneligibles(false) }
            onDragStart={(event) => { onDragStart(event, index); }}
            onDragEnd={() => { onDragEnd(); }}
            onDragOver={() => { onDragOver(index); }}
            onDrop={() =>  { onDrop(); }}
          >
            <DragIndicator/>
          </IconButton>
          { isEditing ? (
            <InputBase
              autoFocus
              value={editValue}
              onChange={(event) => setEditValue(event.target.value)}
              onKeyDown={handleEditKeyDown}
              onBlur={commitEdit}
              onClick={(event) => event.stopPropagation()}
              sx={styles.editInput}
            />
          ) : (
            <ListItemText tabIndex={0} id={`${row.name}`} primary={row.name} sx={styles.listItemText} />
          )}
          { row.isRenamed && !isHovered && !isEditing && (
            <Box title='Renamed from default' sx={styles.renamedDot} />
          )}
          { canRename && isHovered && !isEditing && (
            <Box sx={styles.rowActionsContainer}>
              { row.isRenamed && (
                <IconButton aria-label='Reset ineligible setting name' onClick={handleResetClick} sx={styles.hoverIcon}>
                  <RestartAltOutlinedIcon fontSize='small'/>
                </IconButton>
              )}
              <IconButton aria-label='Edit ineligible setting name' onClick={startEdit} sx={styles.hoverIcon}>
                <EditOutlinedIcon fontSize='small'/>
              </IconButton>
            </Box>
          )}
            <Box
              tabIndex={0}
              role="switch"
              aria-checked={!row.disabled ? 'true' : 'false'}
              aria-label={!row.disabled ? 'Toggle On button' : 'Toggle Off button'}
            >
              <Switch
                tabIndex={-1}
                checked={!row.disabled}
                inputProps={{
                  'aria-label': !row.disabled ? 'Toggle On button' : 'Toggle Off button',
                }}
                onClick={(event) => onItemToggle(event, index)}
                disabled={!(permissions as IIneligibleSettingsPermissions).canActivateDeactivateIneligible}
              />
            </Box>
        </ListItemButton>
      </ListItem>
    );
  };

  return renderListItem();
}

/**
 * This Component represents a draggable list for Ineligibles.
 * @param props The props for the DraggableList component.
 * @returns The DraggableList component JSX.
 */
const DraggableList = (props: DraggableListProps) => {
  const { formik, onItemToggle, onItemDrop, onRenameCommit, onResetClick, draggableListItems, setDraggableListItems } = props;
  const { isIneligibleDetailsLoading, hideInactiveIneligiblesToggle, setIsHidingInactiveIneligibles }  = useContext(IneligibleSettingsContext) as IIneligibleSettingsContext;
  const [draggedListItem, setDraggedListItem] = useState<DraggableListItem | null>(null);

  /**
   * This function handles the item toggle event.
   * @param event The mouse event.
   * @param index The index of the item.
   */
  const handleItemToggle = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    event.stopPropagation(); /** prevents onClick of parent component (ListItemButton) */
    onItemToggle(draggableListItems[index]);
  };

  /**
   * This function handles the drag start event.
   * @param event The drag event.
   * @param index The index of the item.
   */
  const handleDragStart = (event: DragEvent<HTMLButtonElement>, index: number) => {
    const parentElement = event.currentTarget.parentElement as NonNullable<HTMLElement>;
    const parentNode = event.currentTarget.parentNode as NonNullable<ParentNode>;
    const parentElementOfParentNode = parentNode.parentElement as NonNullable<HTMLElement>;

    setDraggedListItem(draggableListItems[index]);

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/html', parentElement.outerHTML);
    event.dataTransfer.setDragImage(
      parentElementOfParentNode,
      (parentElementOfParentNode.clientWidth * 2) - 50,
      parentElementOfParentNode.clientHeight
    );
  };

  /**
   * This function handles the drag end event.
   */
  const handleDragEnd = () => {
    draggedListItem !== null && onItemDrop(draggedListItem);
    setDraggedListItem(null);
  };

  /**
   * This function handles the drag over event.
   * @param hoveredIndex The index of the hovered item.
   */
  const handleDragOver = (hoveredIndex: number) => {
    const nonNullableDraggedListItem = draggedListItem as NonNullable<DraggableListItem>;
    const hoveredItem = draggableListItems[hoveredIndex];
    if (isObjectsEqual(nonNullableDraggedListItem, hoveredItem)) { return; } // no change in items' order
    const rearrangedDraggedListItems = draggableListItems.filter(item => !isObjectsEqual(item, nonNullableDraggedListItem)); /* remove dragged item */
    rearrangedDraggedListItems.splice(hoveredIndex, 0, nonNullableDraggedListItem); /* add dragged item back to the given index to be dropped */

    /* swap AC and DLQ if DLQ is after AC as AC should be always after DLQ */
    const acIndex = rearrangedDraggedListItems.findIndex((item => item.value === 'AC'));
    const dlqIndex = rearrangedDraggedListItems.findIndex((item => item.value === 'DLQ'));
    if (acIndex < dlqIndex) {
      const dlqItem = rearrangedDraggedListItems.splice(dlqIndex, 1, rearrangedDraggedListItems[acIndex])[0]; /* swapping with splice */
      rearrangedDraggedListItems[acIndex] = dlqItem;
    }
    setDraggableListItems(rearrangedDraggedListItems);
  };

  /**
   * This function handles the drop event.
   */
  const handleDrop = () => {
    const nonNullableDraggedListItem = draggedListItem as NonNullable<DraggableListItem>;
    onItemDrop(nonNullableDraggedListItem);
  };

  /**
   * This function checks if an ineligible item has errors.
   * @param item The draggable list item.
   * @returns True if the item has errors, false otherwise.
   */
  const isItemWithError = (item: DraggableListItem) => {
    const formikErrors = formik.errors.ineligibleSettingDetails as FormikErrors<IIneligibleSettingDetail>[];

    const ineligibleSettingDetailIndex = props.formik.values.ineligibleSettingDetails.findIndex(detail => detail.ineligibleSetting.code === item.value);

    if (formikErrors === undefined || ineligibleSettingDetailIndex === NON_EXISTING) { return false; }

    const formikErrorForIneligibleSettingDetail = formikErrors[ineligibleSettingDetailIndex];
    const hasIneligibleRuleErrors = checkRuleErrors(formikErrorForIneligibleSettingDetail);
    const hasIneligibleRuleOverrideErrors = checkOverrideErrors(formikErrorForIneligibleSettingDetail);
    if (formikErrorForIneligibleSettingDetail === undefined || formikErrorForIneligibleSettingDetail === null) { return false; }
    return hasIneligibleRuleErrors || hasIneligibleRuleOverrideErrors;
  };

  /**
   * This function checks if there are rule errors.
   * @param formikErrorForIneligibleSettingDetail Formik errors for an ineligible setting detail.
   * @returns True if there are rule errors, false otherwise.
   */
  const checkRuleErrors = (formikErrorForIneligibleSettingDetail: FormikErrors<IIneligibleSettingDetail>) => {
    return formikErrorForIneligibleSettingDetail?.ineligibleRule !== undefined && Object.keys(formikErrorForIneligibleSettingDetail.ineligibleRule).length > 0;
  };

  /**
   * This function gets the rendered list of draggable items.
   * @returns The rendered list of draggable items.
   */
  const getRenderedList = () => {
    if (isIneligibleDetailsLoading) {
      return (
        <Stack sx={styles.skeletonRowContainer} data-testid='loading-for-ineligible-list'>
          { [...Array(16).keys()].map(number => <Skeleton key={number} animation='wave' sx={styles.skeletonRow}/>) }
        </Stack>
      );
    }
    return (
      <List
        onDragOver={(event) => event.preventDefault() /* prevent ghost image to bounce back */ }
        onMouseLeave={() => { setIsHidingInactiveIneligibles(hideInactiveIneligiblesToggle) }}
      >
        {
          draggableListItems
            .map((item: DraggableListItem, index) => (
              <Row
                formik={formik}
                key={item.recordId}
                row={item}
                index={index}
                isDragging={isObjectsEqual(item, draggedListItem)}
                hasError={isItemWithError(item)}
                onItemToggle={handleItemToggle}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onRenameCommit={onRenameCommit}
                onResetClick={onResetClick}
              />
            ))
        }
      </List>
    );
  };

  return (
    <Box> { getRenderedList() } </Box>
  );
}

export default DraggableList;
